import { useEffect, useState } from 'react';
import { Stack, breakpoints, Inline, LinkButton } from '@etg/wings';
import { useDateFormat, useProperty, useTranslation } from '@eti/providers';
import { useFormContext } from 'react-hook-form';
import { css } from '@eti/styles';
import { m as motion } from 'framer-motion';
import { CaretDown } from '@phosphor-icons/react';
import RebookingBanner from '../../../common/components/RebookingBanner';
import { tripTypes } from '../../../constants/tripTypesConstants';
import { searchForm } from '../utils/searchForm';
import { SINGLE_BOUND } from '../constants/formFieldNames';
import { useToggle } from '../../../common/custom-hooks/toggle';
import { RESULT_PAGE_VERSION_3 } from '../../../pages/result/constants/resultConstants';
import { TRIP_TYPE } from '../constants/formFields';
import type { DateFormat } from '../types/dateFormat';
import type {
  RebookingData,
  SearchFormValues,
} from '../../../common/search-form-panel/SearchFormNewContainer';
import {
  extendedSearchFormStyles,
  iconStyles,
  linkButtonStyles,
  motionVariantsExtendedOptions,
  motionVariantsForDatesAndPassengers,
  StyledStack,
} from './styles/searchFormStyles';
import PassengerAndCabinClassNew from './PassengersAndCabinClassNew';
import DatesNew from './DatesNew';
import SubmitButtonNew from './SubmitButtonNew';
import BoundNew from './BoundNew';
import TripTypesNew from './TripTypesNew';
import BoundsNew from './BoundsNew';
import DirectFlightCheckBoxNew from './DirectFlightCheckBoxNew';
import StopoverSelectNew from './StopoverSelectNew';
import ExtendedSearchFormFieldsNew from './ExtendedSearchFormFieldsNew';
import ZeroWidthSpace from './ZeroWidthSpace';

const { DEPARTURE_DATE } = searchForm.formFieldNames;

const formWrapperStyles = (isMultiStop: boolean) => css`
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (min-width: ${breakpoints._768}) {
    flex-direction: ${isMultiStop ? 'column' : 'row'};
  }
`;

type SearchFormNewProps = {
  departureDateErrorMessage: string;
  isStartPage: boolean;
  rebookingData: RebookingData;
  returnDateErrorMessage: string;
  setDepartureDateErrorMessage: (message: string) => void;
  setReturnDateErrorMessage: (message: string) => void;
  shouldShowRebookingBanner: boolean;
};

const SearchFormNew = ({
  departureDateErrorMessage,
  isStartPage,
  rebookingData,
  returnDateErrorMessage,
  setDepartureDateErrorMessage,
  setReturnDateErrorMessage,
  shouldShowRebookingBanner,
}: SearchFormNewProps) => {
  const { p } = useProperty();
  const { t } = useTranslation();
  const { date } = useDateFormat() as { date: DateFormat };
  const { watch } = useFormContext<SearchFormValues>();
  const [
    selectedTripType,
    origin,
    destination,
    returnTimeOfDay,
    departureTimeOfDay,
    selectedCarriers,
  ] = watch([
    'tripType',
    'singleBound.origin',
    'singleBound.destination',
    'singleBound.returnTimeOfDay',
    'singleBound.departureTimeOfDay',
    'carrierCodes',
  ]);
  const [isDatePassengerFieldsOpen, setIsDatePassengerFieldsOpen] = useState(
    Boolean(origin && destination),
  );
  const { isOpen: isMoreOptionsOpen, handleToggle } = useToggle(
    Boolean(returnTimeOfDay || departureTimeOfDay || selectedCarriers),
  );
  const formConstraints = rebookingData?.formConstraints || null;
  const isMultiStop = selectedTripType === tripTypes.MULTI_STOP;

  useEffect(() => {
    if (origin && destination) {
      setIsDatePassengerFieldsOpen(true);
    }
  }, [origin, destination]);
  const getFieldName = (fieldName: string) =>
    selectedTripType !== tripTypes.MULTI_STOP ? `${SINGLE_BOUND}.${fieldName}` : '';
  const isExtendedSearchFormEnabled = p('SearchForm.ShowExtendedForm.Enabled');
  const searchFormStopoverEnabled = p('Searchform.Stopover.Enabled');
  const resultPageVersion = String(p('Result.Page.Version'));
  const isStopoverEnabled =
    searchFormStopoverEnabled && resultPageVersion === RESULT_PAGE_VERSION_3;
  const shouldShowDateAndPassengerFields = isDatePassengerFieldsOpen || !isStartPage;

  return (
    <>
      {rebookingData && shouldShowRebookingBanner && (
        <RebookingBanner rebookingData={rebookingData} shouldShowRebookingCriteria />
      )}
      <Stack spacing={24}>
        <TripTypesNew fieldName={TRIP_TYPE} />
        <div className={formWrapperStyles(isMultiStop)}>
          <StyledStack spacing={shouldShowDateAndPassengerFields || isMultiStop ? 24 : 0}>
            {!isMultiStop ? (
              <>
                <BoundNew
                  boundIndex={0}
                  dateFormat={date}
                  daysFromTodayForValidDepartureDate={0}
                  departureDateErrorMessage={departureDateErrorMessage}
                  returnDateErrorMessage={returnDateErrorMessage}
                  selectedTripType={selectedTripType}
                  setDepartureDateErrorMessage={setDepartureDateErrorMessage}
                  setReturnDateErrorMessage={setReturnDateErrorMessage}
                  shouldOpenDatePassengerFields={setIsDatePassengerFieldsOpen}
                />
                <motion.div
                  animate={shouldShowDateAndPassengerFields ? 'visible' : 'hidden'}
                  data-testid="date-time-animation-wrapper"
                  initial={false}
                  variants={motionVariantsForDatesAndPassengers}
                >
                  <Inline
                    collapseBelow={breakpoints._768}
                    spacing={{
                      [breakpoints._0]: 24,
                      [breakpoints._768]: 48,
                    }}
                  >
                    <DatesNew
                      boundIndex={0}
                      dateFormat={date}
                      daysFromTodayForValidDepartureDate={0}
                      departureDateErrorMessage={departureDateErrorMessage}
                      departureDateName={getFieldName(DEPARTURE_DATE)}
                      returnDateErrorMessage={returnDateErrorMessage}
                      selectedTripType={selectedTripType}
                      setDepartureDateErrorMessage={setDepartureDateErrorMessage}
                      setReturnDateErrorMessage={setReturnDateErrorMessage}
                    />
                    <PassengerAndCabinClassNew formConstraints={formConstraints} />
                  </Inline>
                  {isExtendedSearchFormEnabled && (
                    <div className={extendedSearchFormStyles}>
                      <Inline spacing={8}>
                        <LinkButton
                          className={linkButtonStyles}
                          data-testid="more-search-options-button"
                          onClick={handleToggle}
                        >
                          {t('Searchform.MoreSearchOptions.Link.Text')}
                          <CaretDown
                            className={iconStyles(isMoreOptionsOpen)}
                            size={20}
                            weight="light"
                          />
                        </LinkButton>
                      </Inline>
                      <motion.div
                        animate={isMoreOptionsOpen ? 'show' : 'hide'}
                        data-testid="more-options-animation-wrapper"
                        initial={false}
                        variants={motionVariantsExtendedOptions}
                      >
                        <Stack spacing={24}>
                          <ExtendedSearchFormFieldsNew selectedTripType={selectedTripType} />
                          {!isStopoverEnabled && <DirectFlightCheckBoxNew />}
                        </Stack>
                      </motion.div>
                    </div>
                  )}
                </motion.div>
                {isStopoverEnabled && (
                  <StopoverSelectNew isDatePassengerFieldsOpen={isDatePassengerFieldsOpen} />
                )}
              </>
            ) : (
              <>
                <BoundsNew
                  dateFormat={date}
                  daysFromTodayForValidDepartureDate={0}
                  departureDateErrorMessage={departureDateErrorMessage}
                  returnDateErrorMessage={returnDateErrorMessage}
                  selectedTripType={selectedTripType}
                  setDepartureDateErrorMessage={setDepartureDateErrorMessage}
                  setReturnDateErrorMessage={setReturnDateErrorMessage}
                />
                <Inline
                  collapseBelow={breakpoints._768}
                  spacing={{
                    [breakpoints._0]: 24,
                    [breakpoints._768]: 48,
                  }}
                >
                  {!isStopoverEnabled && (
                    <Stack spacing={4}>
                      <ZeroWidthSpace isDesktopOnly />
                      <DirectFlightCheckBoxNew align="center" />
                    </Stack>
                  )}
                  <PassengerAndCabinClassNew formConstraints={formConstraints} />
                  <SubmitButtonNew isMultiStop={isMultiStop} />
                </Inline>
                {isStopoverEnabled && <StopoverSelectNew />}
              </>
            )}
          </StyledStack>
          {!isMultiStop && (
            <Stack spacing={4}>
              <SubmitButtonNew
                isDatePassengerFieldsOpen={isDatePassengerFieldsOpen}
                isStopoverEnabled={isStopoverEnabled}
              />
            </Stack>
          )}
        </div>
      </Stack>
    </>
  );
};

export default SearchFormNew;
